import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { setCircularLoading } from "../../../reducers/UiReducer";
import { getService } from "../../../reducers/service";
import { serviceNames } from "../../../utils/constants";
import { notify } from "../../../reducers/NotifierReducer";
import { PATH_TRAINING_PARTICIPANTS } from "../../../utils/routeConstants";
import { mapDictionaryOptions } from "../../../utils/functions";

export const useParticipantData = (id, reset, courseId = null) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userAgency = useSelector((state) => state.user.userAgency);
  const [loaded, setLoaded] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [courses, setCourses] = useState([]);
  const dictionary = useSelector((state) => state.dictionary.dictionary);
  const tagTypeOptions = mapDictionaryOptions(dictionary.codePerTagTypes);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        dispatch(setCircularLoading(true));
        setLoaded(false);

        const services = {
          distinctService: getService(serviceNames.trainingCourseDistinct),
          employees: getService(serviceNames.personnel),
          courses: getService(serviceNames.trainingCourse),
          participants: getService(serviceNames.perTraining),
        };

        const results = await Promise.all([
          services.distinctService.find({
            query: { column: ["location", "category"], agencyId: userAgency },
          }),
          services.employees.find({
            query: {
              $limit: -1,
              isDeleted: false,
              $sort: { lastName: 1, firstName: 1, middleName: 1 },
              agencyId: userAgency,
            },
          }),
          services.courses.find({
            query: { $limit: -1, isDeleted: false, agencyId: userAgency },
          }),
        ]);

        let filteredEmployees = results[1];

        if (!id && courseId) {
          const participants = await services.participants.find({
            query: { perTrainingCourseId: courseId, $limit: -1 },
          });

          const existingEmployeeNums = new Set(
            participants.map((p) => p.employeeNum)
          );

          filteredEmployees = results[1].filter(
            (emp) => !existingEmployeeNums.has(emp.employeeNum)
          );
        }

        if (isMounted) {
          setLocationOptions(results[0]?.location);
          setCategoryOptions(results[0]?.category);
          setEmployees(filteredEmployees);
          setCourses(results[2]);
        }

        if (id) {
          const service = getService(serviceNames.perTraining);

          const result = await service.get(id);
          if (isMounted && result) {
            reset({
              id: result.id || null,
              employeeNum: result.employeeNum || "",
              courseNumber: result.courseNumber || "",
              course: result.course || "",
              courseDate: result.courseDate
                ? moment(result.courseDate).format("MM/DD/YYYY")
                : "",
              attendedDate: result.attendedDate
                ? moment(result.attendedDate).format("MM/DD/YYYY")
                : "",
              courseLocation: result.courseLocation || "",
              dateRegistered: result.dateRegistered
                ? moment(result.dateRegistered).format("MM/DD/YYYY")
                : "",
              hours: result.hours || "",
              grade: result.grade || "",
              approved: result.approved || false,
              checkSent: result.checkSent || false,
              dateCheckSent: result.dateCheckSent
                ? moment(result.dateCheckSent).format("MM/DD/YYYY")
                : "",
              category: result.category || "",
              approvedBy: result.approvedBy || "",
              comments: result.comments || "",
              participantNumber: result.participantNumber || null,
            });
          }
        }
      } catch (error) {
        console.error(error);
        if (isMounted) {
          dispatch(notify("Failed to fetch training participant", "error"));
          history.push(PATH_TRAINING_PARTICIPANTS);
        }
      } finally {
        dispatch(setCircularLoading(false));
        setLoaded(true);
      }
    };

    init();
    return () => {
      isMounted = false;
    };
  }, [id, dispatch, reset, userAgency]);

  return {
    loaded,
    locationOptions,
    categoryOptions,
    employees,
    courses,
    tagTypeOptions,
  };
};
